import React, {useMemo, useReducer} from 'react';
import {useSelector} from 'react-redux';

import {blockResultSelector} from '../../../store/reducers/blockResultReducer';
import {ApiResult, ContentCard, ContentToolbar, NoIntegration} from '../components';
import {BaseBlock, ContentActions, ContentOptions} from '../types';
import {defaultContentActions, defaultContentOptions} from '../constants';
import {
  useCreateBlock,
  useExecuteRequest,
  useGetIntegrations,
  useInitBlock,
  useLoadResult,
  useUpdateBlockContentJson
} from '../hooks';
import {integrationToSelectOption, shouldLoadResult} from '../utils';
import {contentReducer, setContentFormKeyValue} from '../store';
import {initialContentState} from './store';
import {IntegrationForm} from './IntegrationForm';
import {createDefaultContent} from './utils';

import './CustomApp.scss';

interface CustomAppProps {
  content: BaseBlock;
  options?: ContentOptions;
  actions?: ContentActions;
}

export const CustomApp = ({
  content,
  options = defaultContentOptions,
  actions = defaultContentActions,
}: CustomAppProps) => {
  options = useMemo((): ContentOptions => ({...defaultContentOptions, ...options}), [options?.readOnly]);
  actions = useMemo((): ContentActions => ({...defaultContentActions, ...actions}), []);

  const [contentState, contentDispatch] = useReducer(contentReducer, initialContentState);

  const uid = content.uid;
  const result = useSelector(blockResultSelector(uid as string, content.result_id as string));
  const resultId: string | null = content.result_id || result?.key || null;

  const {executing, executeRequest} = useExecuteRequest(uid);
  const {integrations, hasIntegrations} = useGetIntegrations(content.type);
  const {updating, updateBlockContentJson} = useUpdateBlockContentJson(uid, options.readOnly, options.autosave);
  const selectedIntegration = useMemo(
    () => integrations.find((i: any) => i.uid === contentState.form.integration_id),
    [contentState.form.init, contentState.form.integration_id, integrations?.length]
  );

  useCreateBlock(content, createDefaultContent());
  useInitBlock(uid, content, contentDispatch);
  useLoadResult(uid, resultId,
    options.loadResult !== undefined ? options.loadResult : shouldLoadResult(content.result_id, result)
  );

  const onExecute = () => {
    const block: any = {
      content_json: {
        integration_id: contentState.form.integration_id,
        form_inputs: contentState.form.form_inputs,
      }
    };
    executeRequest(block);
  };

  const onIntegrationIdChange = (integrationId: string) => {
    contentDispatch(setContentFormKeyValue('integration_id', integrationId));
    updateBlockContentJson('integration_id', integrationId);
  };

  const onFormInputChange = (key: string, value: any) => {
    const formInputs = {
      ...contentState.form.form_inputs,
      [key]: value,
    };

    contentDispatch(setContentFormKeyValue('form_inputs', formInputs));
    updateBlockContentJson('form_inputs', formInputs);
  }

  return (
    <ContentCard
      key={content.id}
      type={content.type}
      contentId={contentState.init ? uid : null}
      disabled={!hasIntegrations || options.readOnly}
      noContentElement={hasIntegrations ? null : <NoIntegration
        contentId={uid as string}
        type={content.type}
        onDelete={actions.onDelete}
        showDelete={options.showDelete}
      />}
      allowCollapse={options.allowCollapse}
      showHeader={options.showHeader}
    >
      <div className="content__custom-app">
        <ContentToolbar
          show={options?.showToolbar}
          contentId={uid as string}
          updating={updating}
          executing={executing}
          onExecute={onExecute}
          selected={contentState.form.integration_id}
          onSelect={onIntegrationIdChange}
          options={integrations.map(integrationToSelectOption)}
          onDelete={actions.onDelete}
          showDelete={options.showDelete}
          showSave={false}
          disabled={options.readOnly || false}
          showPreview={false}
        />
        <div className="content-card__content__form">
          <div className="content__custom-app__form">
            <IntegrationForm
              integration={selectedIntegration}
              onChange={onFormInputChange}
              values={contentState.form.form_inputs || {}}
              disabled={options.readOnly}
            />
          </div>
          <ApiResult
            result={result}
            failedTitle="API Request run failed"
          />
        </div>
      </div>
    </ContentCard>
  );
}

export default CustomApp;
